/**
 * TenantContext — Multi-tenant context provider
 * Story SAAS-3.2 + SAAS-7.1+7.3 / EPIC-SAAS-001
 *
 * Modelagem hierárquica (Modelo C):
 *   Organization (cliente SaaS, ex: "Dobra Camisetas Group")
 *   └── Loja (unidade operacional, ex: "Matriz", "EWM", "DBTech")
 *
 * `availableTenants` lista as LOJAS que o user pode acessar.
 * `activeTenant` é a LOJA ativa atualmente.
 * `currentOrganizationId` é a empresa-pai do user (NULL para super_admin).
 *
 * Não existe troca de loja pelo usuário (removida em 2026-09-17): a loja é
 * atributo do pedido, todo usuário vive no tenant da organização e enxerga
 * todas as lojas. O super_admin opera cross-org; o módulo de estoque resolve
 * a Matriz sozinho (`stock-matriz.ts`).
 */

import { createContext, useContext, useMemo } from 'react';
import { useAuth, type TenantSummary } from './AuthContext';

interface TenantContextType {
  /** Loja ativa (alias retrocompat: activeTenant === activeLoja) */
  activeTenant: TenantSummary | null;
  /** Loja ativa — alias semanticamente correto */
  activeLoja: TenantSummary | null;
  /** Lista de lojas que o user pode acessar */
  availableTenants: TenantSummary[];
  /** ID da organization-pai do user (NULL para super-admin) */
  currentOrganizationId: string | null;
  /** True se user é super-admin (cross-organization) */
  isSuperAdmin: boolean;
  /** True se user é org-admin (cross-loja-same-org) */
  isOrgAdmin: boolean;
  /** True enquanto AuthContext ainda não resolveu o role */
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextType>({
  activeTenant: null,
  activeLoja: null,
  availableTenants: [],
  currentOrganizationId: null,
  isSuperAdmin: false,
  isOrgAdmin: false,
  isLoading: true,
});

export const useTenant = () => useContext(TenantContext);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { userRole, roleLoading } = useAuth();

  const availableTenants: TenantSummary[] = useMemo(
    () => userRole?.availableTenants ?? [],
    [userRole?.availableTenants]
  );

  const activeTenant: TenantSummary | null = useMemo(() => {
    const activeId = userRole?.activeTenantId;
    if (!activeId) return null;
    return availableTenants.find(t => t.id === activeId) ?? null;
  }, [userRole?.activeTenantId, availableTenants]);

  const isSuperAdmin = userRole?.isSuperAdmin === true;
  const isOrgAdmin = userRole?.isOrgAdmin === true;
  const currentOrganizationId = userRole?.organizationId ?? null;

  const value: TenantContextType = useMemo(
    () => ({
      activeTenant,
      activeLoja: activeTenant,
      availableTenants,
      currentOrganizationId,
      isSuperAdmin,
      isOrgAdmin,
      isLoading: roleLoading,
    }),
    [activeTenant, availableTenants, currentOrganizationId, isSuperAdmin, isOrgAdmin, roleLoading]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
